import { memo } from 'react';
import { LayoutGrid, Wand2, Plus } from 'lucide-react';
import { spacing } from '../../constants/designTokens';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * ModeToggle Component
 *
 * Toggle between Preset and Creative modes.
 * Shows a "New" button in creative mode when a pattern has been generated.
 */
const ModeToggle = memo(({ mode, onModeChange, onReset, hasPattern = false }) => {
  const { colors } = useTheme();

  // Show "New" button when in creative mode and a pattern exists
  const showNewButton = mode === 'creative' && hasPattern;

  const buttonBase = {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    height: '36px',
    padding: '0 14px',
    fontSize: '0.8125rem',
    fontWeight: 500,
    letterSpacing: '-0.005em',
    fontFamily: 'Sohne, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    outline: 'none',
  };

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      marginBottom: spacing.xxxl,
      gap: '6px'
    }}>
      <button
        onClick={() => {
          onModeChange('preset');
          onReset();
        }}
        style={{
          ...buttonBase,
          color: mode === 'preset' ? colors.background : colors.foreground,
          background: mode === 'preset' ? colors.foreground : 'transparent',
          border: mode === 'preset' ? 'none' : `1px solid ${colors.border}`,
        }}
        onMouseEnter={(e) => {
          if (mode !== 'preset') {
            e.currentTarget.style.background = colors.backgroundOffset;
          } else {
            e.currentTarget.style.opacity = '0.85';
          }
        }}
        onMouseLeave={(e) => {
          if (mode !== 'preset') {
            e.currentTarget.style.background = 'transparent';
          } else {
            e.currentTarget.style.opacity = '1';
          }
        }}
      >
        <LayoutGrid size={14} />
        Presets
      </button>

      <button
        onClick={() => onModeChange('creative')}
        style={{
          ...buttonBase,
          color: mode === 'creative' ? colors.background : colors.foreground,
          background: mode === 'creative' ? colors.foreground : 'transparent',
          border: mode === 'creative' ? 'none' : `1px solid ${colors.border}`,
        }}
        onMouseEnter={(e) => {
          if (mode !== 'creative') {
            e.currentTarget.style.background = colors.backgroundOffset;
          } else {
            e.currentTarget.style.opacity = '0.85';
          }
        }}
        onMouseLeave={(e) => {
          if (mode !== 'creative') {
            e.currentTarget.style.background = 'transparent';
          } else {
            e.currentTarget.style.opacity = '1';
          }
        }}
      >
        <Wand2 size={14} />
        Create
      </button>

      {/* New Pattern Button - appears when in creative mode with a generated pattern */}
      {showNewButton && (
        <button
          onClick={onReset}
          style={{
            ...buttonBase,
            color: colors.foreground,
            background: 'transparent',
            border: `1px dashed ${colors.border}`,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = colors.backgroundOffset;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
          }}
        >
          <Plus size={14} />
          New
        </button>
      )}
    </div>
  );
});

export default ModeToggle;
